/**
 * Copyright (c) S2 Partnership Ltd. 2022
 *
 * A service that sets up, stores and retrieves data items on the device.
 * Keys are defined for key data models.
 *
 * @summary Service to store and retrieve data item son the device.
 * @author S2
 *
 * Created at     : 17-06-2021
 */

import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { BehaviorSubject } from 'rxjs';
import { AuthenticatedUser } from './../../app/view-models/authenticated-user';
import { EncryptionService } from './encryption.service';

@Injectable()
export class StorageService {

  private authenticatedUser: AuthenticatedUser;
  items = [
    new StorageItem(StorageKey.ACTION_PROGRESS_CATEGORIES, true),
    new StorageItem(StorageKey.ACTION_PRIORITIES, true),
    new StorageItem(StorageKey.ALLOWED_FILE_EXTENSION, false),
    new StorageItem(StorageKey.ALLOWED_IMAGE_EXTENSION, false),
    new StorageItem(StorageKey.AUDITS, true),
    new StorageItem(StorageKey.AUTHENTICATED_USER, false),
    new StorageItem(StorageKey.LOGIN_DATA, true),
    new StorageItem(StorageKey.MODULE_ACTIONS, true),
    new StorageItem(StorageKey.MODULE_INSPECTIONS, true),
    new StorageItem(StorageKey.PROPERTIES, true),
    new StorageItem(StorageKey.SERVER_SETTINGS, true),
    new StorageItem(StorageKey.SMART_SYNC_SELECTIONS, true),
    new StorageItem(StorageKey.USERS, true),
    new StorageItem(StorageKey.ACTIVE_PROPERTY, true),
    new StorageItem(StorageKey.DATA_ON_DEVICE, true),
    new StorageItem(StorageKey.SMART_SYNC_PROPERTIES, true),
    new StorageItem(StorageKey.APP_BRANDING, false),
    new StorageItem(StorageKey.PROPERTIES_IMAGES, false, true),
    new StorageItem(StorageKey.WELCOME_MESSAGE, true, false),
    new StorageItem(StorageKey.BIOMETRIC_USER, false),
    new StorageItem(StorageKey.LANGUAGE, false),
    new StorageItem(StorageKey.SCHEDULE_TOGGLE_VALUES, true, false)
  ];

  /**
   * Standard Angular Constructor.
   *
   * @ignore
   */
  constructor(
    /* eslint-disable */
    private encryptionService: EncryptionService
    /* eslint-enable */
  ) {
  }

  /**
   * Gets the native storage key.
   *
   * @arg {StorageKey} key The storage key in local storage.
   * @return {string} The native key for storage.
   */
  private getNativeKey(key: StorageKey) {
    if (this.findItem(key).userScope) {
      return `${this.authenticatedUser.companyId}-${this.authenticatedUser.username}-${key}`;
    } else if (this.findItem(key).clientScope && this.authenticatedUser) {
      return `${this.authenticatedUser.companyId}-${key}`;
    } else {
      return key;
    }
  }

  /**
   * Calls setSubjectsFromStoredValues.
   */
  async onAppInitAfterLogin() {
    await this.setSubjectsFromStoredValues(this.items.filter(item => item.userScope));
  }

  /**
   * On app init set the authenticated user.
   */
  async onAppInit() {
    this.findItem(StorageKey.AUTHENTICATED_USER).subject.subscribe((user: AuthenticatedUser) => {
      this.authenticatedUser = user;
    });
    this.setSubjectsFromStoredValues(this.items.filter(item => !item.userScope));
  }

  /**
   * Sets the subjects from stored values.
   *
   * @arg {StorageItem[]} items Storage item.
   */
  private async setSubjectsFromStoredValues(items: StorageItem[]) {
    for (const item of items) {
      item.subject.next(await this.getStoredValueForKey(item.key));
    }
  }

  /**
   * Gets the stored value for key.
   *
   * @arg {StorageKey} key Storage key.
   */
  private async getStoredValueForKey(key: StorageKey) {
    const raw = await Preferences.get({ key: this.getNativeKey(key) });
    if (!raw || !raw.value) {
      return null;
    }
    const decryptedValue = this.encryptionService.decrypt(raw.value);
    return raw ? JSON.parse(decryptedValue) : null;
  }

  /**
   * Finds storage item.
   *
   * @arg {StorageKey} key Storage key.
   * @return {any} The storage item found.
   */
  findItem(key: StorageKey): any {
    return this.items.find(item => item.key === key);
  }

  /**
   *
   * This method is called when user has performed actions that require saving
   * locally on the device and should persist after app closed.
   *
   * @arg {StorageKey} key The key of the storage object that has to be set.
   * @arg {any} object The data object to be saved.
   */
  async set<T>(
    key: StorageKey,
    object: T
  ) {
    if (typeof object !== 'object') {
      throw new Error('Only objects can stored');
    }

    let value = '';

    if (Array.isArray(object)) {
      let items: any[];
      await this.getStoredValueForKey(key).then(storedItems => items = storedItems || []);

      for (const objectItem of object) {
        const foundItemIndex = items.findIndex(item => item.id === objectItem.id);
        if (foundItemIndex > -1) {
          items.splice(foundItemIndex, 1);
        }
      }

      const wholeObject = object.concat(items);
      this.findItem(key).subject.next(wholeObject);
      value = JSON.stringify(wholeObject);
    } else {
      this.findItem(key).subject.next(object);
      value = JSON.stringify(object);
    }

    await Preferences.set({
      key: this.getNativeKey(key),
      value: this.encryptionService.encrypt(value)
    });
  }

  /**
   * Removes a data item from the device date, for the key handed to this method.
   *
   * @arg {StorageKey} key The key for the data item that needs to be removed.
   */
  async remove(
    key: StorageKey
  ) {
    await Preferences.remove({ key: this.getNativeKey(key) });
    this.findItem(key).subject.next(undefined);
  }

}

/**
 * Class that defines storage item data models for saving on the device.
 */
export class StorageItem {
  subject: BehaviorSubject<any>;
  /**
   * Standard Angular Constructor.
   *
   * @ignore
   */
  constructor(
    /* eslint-disable */
    public key: StorageKey,
    public userScope: boolean,
    public clientScope?: boolean
    /* eslint-enable */
  ) {
    this.subject = new BehaviorSubject<any>(null);
  }
}

/**
 * An enum that defines all the keys for data on storage on a device.
 */
export enum StorageKey {
  ACTION_PROGRESS_CATEGORIES = 'ACTION_PROGRESS_CATEGORIES',
  ACTION_PRIORITIES = 'ACTION_PRIORITIES',
  ALLOWED_FILE_EXTENSION = 'ALLOWED_FILE_EXTENSION',
  ALLOWED_IMAGE_EXTENSION = 'ALLOWED_IMAGE_EXTENSION',
  AUDITS = 'AUDITS',
  AUTHENTICATED_USER = 'AUTHENTICATED_USER',
  LOGIN_DATA = 'LOGIN_DATA',
  MODULE_ACTIONS = 'MODULE_ACTIONS',
  MODULE_INSPECTIONS = 'MODULE_INSPECTIONS',
  PROPERTIES = 'PROPERTIES',
  SERVER_SETTINGS = 'SERVER_SETTINGS',
  SMART_SYNC_SELECTIONS = 'SMART_SYNC_SELECTIONS',
  USERS = 'USERS',
  ACTIVE_PROPERTY = 'ACTIVE_PROPERTY',
  DATA_ON_DEVICE = 'DATA_ON_DEVICE',
  SMART_SYNC_PROPERTIES = 'SMART_SYNC_PROPERTIES',
  APP_BRANDING = 'APP_BRANDING',
  PROPERTIES_IMAGES = 'PROPERTIES_IMAGES',
  WELCOME_MESSAGE = 'WELCOME_MESSAGE',
  BIOMETRIC_USER = 'BIOMETRIC_USER',
  LANGUAGE = 'LANGUAGE',
  SCHEDULE_TOGGLE_VALUES = 'SCHEDULE_TOGGLE_VALUES'
}
